import * as React from 'react';
import { Formik, FormikConfig } from 'formik';
import { makeValidator } from 'mandle';
import { Redirect } from 'react-router-dom';
import { useLocation } from 'react-router';
import parseISO from 'date-fns/parseISO';
import isPast from 'date-fns/isPast';
import * as O from 'fp-ts/Option';
import * as A from 'fp-ts/Array';
import { pipe } from 'fp-ts/lib/function';
import qs from 'qs';

import { ListSelector, ListSelection } from '../../../components/ListSelector';
import {
  AvailabilityResponseEntityFragment,
  AvailabilityStatus,
  FullMatchFragment,
  useSetMatchAvailabilityMutation,
} from '../../../generated/graphql';
import { SubmitButton } from '../../../components/SubmitButton';
import { required2, oneOf } from '../../../validators';
import { formatErrors } from '../../../utils/formatErrors';

import { ButtonLink } from '../../../components/Button';
import { useAlert } from '../../../contexts/AlertContext';
import { makeAvailabilityMap } from '../../../utils/makeAvailabilityMap';
import { AvailabilityStatusDot } from '../../../components/AvailabilityStatus';
import { paths } from '../../../paths';
import { useAuth } from '../../../contexts/AuthContext';
import { TextLink } from '../../../components/ClickableText';
import { RadioChoice } from '../../../components/RadioChoice';
import { InputError } from '../../../components/InputError';
import { isNil } from '../../../utils/isNil';
import { useAnalytics } from '../../../contexts/AnalyticsContext';
import { PlayerAvatar } from '../../../components/PlayerAvatar';
import { NoPlayers } from './NoPlayers';

interface Props {
  match: FullMatchFragment;
}

interface Fields {
  playerId: string;
  matchId: string;
  status: AvailabilityStatus;
}

const availabilities: Record<AvailabilityStatus, AvailabilityStatus> = {
  AVAILABLE: 'AVAILABLE',
  MAYBE: 'MAYBE',
  UNAVAILABLE: 'UNAVAILABLE',
  UNDECLARED: 'UNDECLARED',
};

const validate = makeValidator<Fields>({
  matchId: [required2()],
  playerId: [required2('Select your player')],
  status: [
    oneOf(['AVAILABLE', 'UNAVAILABLE', 'MAYBE'], 'Select your availability'),
  ],
});

const availabilityConfirmationMessage = (
  availability: AvailabilityResponseEntityFragment,
) => {
  switch (availability.status) {
    case 'AVAILABLE':
      return `Saved! ${availability.player.name} is available 😄.`;
    case 'UNAVAILABLE':
      return `Saved! ${availability.player.name} is unavailable 😞.`;
    case 'MAYBE':
      return `Saved! ${availability.player.name} is a maybe 🤔.`;

    default:
      return 'Saved!';
  }
};

const SignInPrompt: React.FC = () => {
  const { pathname } = useLocation();
  const currentPageQueryString = qs.stringify(
    { intended: pathname },
    { addQueryPrefix: true },
  );

  return (
    <p className="text-xs text-grey-600">
      Can't select your player?{' '}
      <TextLink
        to={{ pathname: paths.signIn(), search: currentPageQueryString }}
      >
        Sign in
      </TextLink>{' '}
      to submit your availablilty.
    </p>
  );
};

export const Availability: React.FC<Props> = ({ match }) => {
  const analytics = useAnalytics();
  const alert = useAlert();
  const { user } = useAuth();

  const [setAvailabilityResponse, setAvailability] =
    useSetMatchAvailabilityMutation();

  if (
    isPast(parseISO(match.playedAt)) ||
    !isNil(setAvailabilityResponse.data?.setMatchAvailability?.entity)
  ) {
    return <Redirect to={paths.match.home(match.id)} />;
  }

  return pipe(
    match.team.players,
    O.fromPredicate(A.isNonEmpty),
    O.map((players) => {
      const availabilities = makeAvailabilityMap(match.availabilities);
      return players.map(
        (player): ListSelection => ({
          name: (
            <div className="flex items-center">
              <PlayerAvatar player={player} className="mr-2" />
              <p>
                {player.name}{' '}
                <AvailabilityStatusDot status={availabilities[player.id]} />
              </p>
            </div>
          ),
          searchValue: player.name,
          value: player.id,
          disabled: !isNil(player.user),
          title: !isNil(player.user)
            ? `${player.name} is associated with a user. Log in as this player to set availability`
            : undefined,
        }),
      );
    }),
    O.fold(
      () => <NoPlayers matchId={match.id} />,
      (playerOptions) => {
        const initialValues: Fields = {
          playerId: match.team.player ? match.team.player.id : '',
          matchId: match.id,
          status: 'UNDECLARED',
        };

        const submit: FormikConfig<Fields>['onSubmit'] = (input, actions) => {
          analytics.track('Form Submitted', { form: 'availability' });
          return setAvailability({ input }).then((res) => {
            if (res.data?.setMatchAvailability?.validation) {
              const errors = formatErrors(
                res.data.setMatchAvailability.validation,
              );
              analytics.track('Data Submit Failed', {
                form: 'availability',
                errors,
              });
              console.log(errors);
              actions.setErrors(errors);
              return;
            }

            if (res.data?.setMatchAvailability?.entity) {
              alert(
                availabilityConfirmationMessage(
                  res.data.setMatchAvailability.entity,
                ),
              );
              analytics.track('Data Submit Succeeded', {
                form: 'availability',
              });
              return;
            }
          });
        };

        return (
          <Formik
            initialValues={initialValues}
            onSubmit={submit}
            validate={validate}
          >
            {({ handleSubmit, values, handleChange, touched, errors }) => (
              <form
                onSubmit={handleSubmit}
                noValidate={true}
                className="space-y-6"
              >
                <p>
                  Let your captain know if you can make this match.{' '}
                  {!match.team.player
                    ? 'Select yourself from the list and set your availablity.'
                    : null}
                </p>
                {!match.team.player ? (
                  <div className="space-y-2">
                    <ListSelector
                      label="Player"
                      onChange={handleChange('playerId')}
                      value={values.playerId}
                      options={playerOptions}
                      error={touched.playerId ? errors.playerId : undefined}
                      showFilter={playerOptions.length > 15}
                    />
                    {isNil(user) ? <SignInPrompt /> : null}
                  </div>
                ) : null}

                <div>
                  <RadioChoice.Container
                    onChange={handleChange('status')}
                    value={values.status}
                  >
                    <RadioChoice.Option
                      value={availabilities.AVAILABLE}
                      checkedClass="bg-green-100"
                    >
                      Available
                    </RadioChoice.Option>
                    <RadioChoice.Option
                      value={availabilities.MAYBE}
                      checkedClass="bg-amber-200"
                    >
                      Maybe
                    </RadioChoice.Option>
                    <RadioChoice.Option
                      value={availabilities.UNAVAILABLE}
                      checkedClass="bg-red-100"
                    >
                      Unavailable
                    </RadioChoice.Option>
                  </RadioChoice.Container>

                  {errors.status && touched.status ? (
                    <InputError className="mt-1">{errors.status}</InputError>
                  ) : null}
                </div>

                <SubmitButton loading={setAvailabilityResponse.fetching}>
                  Save
                </SubmitButton>

                <ButtonLink
                  to={paths.match.home(match.id)}
                  kind="plain"
                  icon="arrow-left"
                >
                  Back to match
                </ButtonLink>
              </form>
            )}
          </Formik>
        );
      },
    ),
  );
};
